/**
 * FHIR constants and query helpers for the Unify messaging feature.
 *
 * Thread model (Communication-based):
 * - Thread root = Communication with identifier 'conversation-topic'. Contains all participants
 *   in `recipient` for queryability, category for thread type, topic.text for group names.
 * - Message = Communication with partOf → thread root. Carries sender, payload, sent.
 * - Sub-thread reply = Communication with partOf → parent message + inResponseTo → parent message.
 *   This keeps sub-thread queries simple (part-of:parentMsg) while preserving FHIR reply semantics.
 * - Attachments use native payload.contentAttachment.
 * - Read/unread: `readByUser` extension (JSON map ref → ISO time) on thread roots. Each send bumps the
 *   sender’s entry to `thread.sent` so senders aren’t unread for their own messages; others stay unread
 *   until their `readByUser` time ≥ `thread.sent` (after opening the thread).
 *   Sub-thread replies do NOT touch the root — they don't affect main-chat unread state or sidebar preview.
 */

import type { MedplumClient } from '@medplum/core';
import type {
  CodeableConcept,
  Communication,
  Group as FhirGroup,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes';
import { formatPractitionerPreferredLabel } from '@unify/fhir-model/resources/practitioner';
import { createSecuredAttachment } from '../../../lib/createSecuredAttachment';

/** Narrow Reference to the union Communication.sender accepts. */
type SenderRef = NonNullable<Communication['sender']>;
type RecipientRef = NonNullable<NonNullable<Communication['recipient']>[number]>;

// ── Constants ────────────────────────────────────────────────────────────────

export const MSG = {
  IDENTIFIER_SYSTEM: 'https://unify.health/fhir/identifier/messaging',
  THREAD_IDENTIFIER_VALUE: 'conversation-topic',
  MESSAGE_IDENTIFIER_VALUE: 'message',
  CATEGORY_SYSTEM: 'https://unify.health/fhir/CodeSystem/messaging-category',
  EXT: {
    UNREAD_COUNT: 'https://unify.health/fhir/messaging/unread-count',
    READ_AT: 'https://unify.health/fhir/messaging/read-at',
    /** JSON object: { "Patient/abc": "2026-01-01T00:00:00.000Z", "Practitioner/xyz": "..." } */
    READ_BY_USER: 'https://unify.health/fhir/messaging/read-by-user',
    SEQUENCE_NUMBER: 'https://unify.health/fhir/messaging/sequence-number',
    DELIVERED_AT: 'https://unify.health/fhir/messaging/delivered-at',
    QUOTED_MESSAGE_ID: 'https://unify.health/fhir/messaging/quoted-message-id',
    QUOTED_SENDER: 'https://unify.health/fhir/messaging/quoted-sender',
    QUOTED_CONTENT: 'https://unify.health/fhir/messaging/quoted-content',
    QUOTED_IMAGE_URL: 'https://unify.health/fhir/messaging/quoted-image-url',
    HAS_UNREAD: 'https://unify.health/fhir/messaging/has-unread',
  },
  CATEGORY: {
    CLIENT: 'client-conversation',
    PROVIDER: 'provider-conversation',
    GROUP: 'space',
    SYSTEM: 'system',
  },
  /** Pre-PRD codes kept for backward compatibility with existing data. */
  LEGACY_CATEGORY: {
    CLIENT: 'client-thread',
    PROVIDER: 'provider-thread',
    GROUP: 'group-thread',
  },
  LEGACY_THREAD_IDENTIFIER_VALUE: 'thread-topic',
} as const;

// ── Group constants ───────────────────────────────────────────────────────────

export const MSG_GROUP = {
  /** Group.type for client conversations (Patient + Practitioner). */
  TYPE_CLIENT: 'person' as const,
  /** Group.type for provider conversations and spaces. */
  TYPE_PROVIDER: 'practitioner' as const,
  PRACTICE_TAG_SYSTEM: 'https://unify.health/fhir/practice-tag',
  SPACE_RULES_EXT: 'https://unify.health/fhir/messaging/space-rules',
  SPACE_ROLE_EXT: 'https://unify.health/fhir/messaging/space-role',
  MANUAL_ADD_EXT: 'https://unify.health/fhir/messaging/manual-add',
} as const;

/**
 * WebSocket subscription criteria for messaging Communications (PRD: practice tag when known).
 * Used where thread roots and messages may both need notification (e.g. space membership).
 */
export function messagingCommunicationSubscriptionCriteria(
  practiceTagCode?: string,
): string {
  if (practiceTagCode) {
    return `Communication?_tag=${MSG_GROUP.PRACTICE_TAG_SYSTEM}|${encodeURIComponent(practiceTagCode)}`;
  }
  return `Communication?identifier=${encodeURIComponent(MSG.IDENTIFIER_SYSTEM)}`;
}

/**
 * Broad per-tab subscription for new chat messages (nav badge + inbox sidebar).
 * Must target `identifier=…|message` — the pre-refactor criteria that reliably delivered
 * message creates. The generic `identifier=system` form does not match message events consistently.
 *
 * Outbound messages often lack `meta.tag`; do not AND with practice-tag here.
 */
export function broadMessagingSubscriptionCriteria(): string {
  return `Communication?identifier=${encodeURIComponent(MSG.IDENTIFIER_SYSTEM)}|${MSG.MESSAGE_IDENTIFIER_VALUE}`;
}

// ── Group helpers ─────────────────────────────────────────────────────────────

export interface CreateConversationGroupParams {
  type: 'person' | 'practitioner';
  members: Reference[];
  name?: string;
  managingEntity?: Reference;
  practiceTagCode?: string;
  spaceRules?: Array<{ type: string; value: string }>;
}

/** Create a FHIR Group resource to serve as the conversation membership container. */
export async function createConversationGroup(
  medplum: MedplumClient,
  params: CreateConversationGroupParams,
): Promise<FhirGroup> {
  const memberEntries: NonNullable<FhirGroup['member']> = params.members.map((ref) => ({
    entity: ref,
  })) as NonNullable<FhirGroup['member']>;

  const meta = params.practiceTagCode
    ? { tag: [{ system: MSG_GROUP.PRACTICE_TAG_SYSTEM, code: params.practiceTagCode }] }
    : undefined;

  const extensions = params.spaceRules?.length
    ? [
        {
          url: MSG_GROUP.SPACE_RULES_EXT,
          extension: params.spaceRules.map((rule) => ({
            url: 'rule',
            extension: [
              { url: 'type', valueCode: rule.type },
              { url: 'value', valueString: rule.value },
            ],
          })),
        },
      ]
    : undefined;

  return medplum.createResource<FhirGroup>({
    resourceType: 'Group',
    type: params.type,
    actual: true,
    active: true,
    ...(params.name ? { name: params.name } : {}),
    ...(params.managingEntity
      ? { managingEntity: params.managingEntity as FhirGroup['managingEntity'] }
      : {}),
    member: memberEntries,
    ...(meta ? { meta } : {}),
    ...(extensions ? { extension: extensions } : {}),
  } as FhirGroup);
}

/** Fetch Group resources where the user is a member (active conversations). */
export async function fetchConversationGroupsForUser(
  medplum: MedplumClient,
  userReference: string,
  practiceTagCode?: string,
): Promise<FhirGroup[]> {
  const params: Record<string, string> = {
    member: userReference,
    active: 'true',
    _count: '100',
  };
  if (practiceTagCode) {
    params._tag = `${MSG_GROUP.PRACTICE_TAG_SYSTEM}|${practiceTagCode}`;
  }
  return medplum.searchResources('Group', params);
}

/** Fetch the conversation header Communication for a given Group. */
export async function fetchConversationHeaderForGroup(
  medplum: MedplumClient,
  groupId: string,
): Promise<Communication | undefined> {
  for (const identifierValue of [MSG.THREAD_IDENTIFIER_VALUE, MSG.LEGACY_THREAD_IDENTIFIER_VALUE]) {
    const results = await medplum.searchResources('Communication', {
      subject: `Group/${groupId}`,
      identifier: `${MSG.IDENTIFIER_SYSTEM}|${identifierValue}`,
      _count: '1',
    });
    if (results[0]) return results[0];
  }
  return undefined;
}

/** Fetch conversation headers for multiple Groups in batch. */
async function fetchConversationHeadersForGroups(
  medplum: MedplumClient,
  groupIds: string[],
): Promise<Communication[]> {
  if (groupIds.length === 0) return [];
  const headers = await Promise.all(
    groupIds.map((gid) => fetchConversationHeaderForGroup(medplum, gid)),
  );
  return headers.filter((h): h is Communication => h != null);
}

// ── Queries ──────────────────────────────────────────────────────────────────

/**
 * Fetch thread roots for the current user using Group-first discovery with legacy fallback.
 * 1. Query Groups the user is a member of.
 * 2. Load linked conversation headers.
 * 3. Fall back to legacy Communication?recipient= for pre-migration threads.
 * 4. Merge and deduplicate.
 */
export async function fetchThreadsForUser(
  medplum: MedplumClient,
  userReference: string,
  practiceTagCode?: string,
): Promise<Communication[]> {
  const [groupHeaders, legacyThreads] = await Promise.all([
    fetchThreadsViaGroups(medplum, userReference, practiceTagCode),
    fetchLegacyThreadsForUser(medplum, userReference),
  ]);

  // Deduplicate: Group-based threads take priority
  const seenIds = new Set(groupHeaders.map((h) => h.id).filter(Boolean));
  const uniqueLegacy = legacyThreads.filter((t) => !t.id || !seenIds.has(t.id));

  const all = [...groupHeaders, ...uniqueLegacy];
  // Sort by sent descending
  all.sort((a, b) => {
    const aMs = a.sent ? new Date(a.sent).getTime() : 0;
    const bMs = b.sent ? new Date(b.sent).getTime() : 0;
    return bMs - aMs;
  });
  return all.slice(0, 100);
}

/** Group-first thread discovery. */
async function fetchThreadsViaGroups(
  medplum: MedplumClient,
  userReference: string,
  practiceTagCode?: string,
): Promise<Communication[]> {
  try {
    const groups = await fetchConversationGroupsForUser(medplum, userReference, practiceTagCode);
    const groupIds = groups.map((g) => g.id).filter(Boolean) as string[];
    return fetchConversationHeadersForGroups(medplum, groupIds);
  } catch {
    return [];
  }
}

/** Legacy: fetch thread roots where the user is listed in Communication.recipient. */
async function fetchLegacyThreadsForUser(
  medplum: MedplumClient,
  userReference: string,
): Promise<Communication[]> {
  return medplum.searchResources('Communication', {
    identifier: `${MSG.IDENTIFIER_SYSTEM}|${MSG.THREAD_IDENTIFIER_VALUE},${MSG.IDENTIFIER_SYSTEM}|${MSG.LEGACY_THREAD_IDENTIFIER_VALUE}`,
    recipient: userReference,
    _sort: '-sent',
    _count: '100',
  });
}

/** Fetch messages within a thread (direct children via partOf). */
/** Drop duplicate rows when search/subscription overlap (same Communication.id). */
export function dedupeCommunicationsById(messages: Communication[]): Communication[] {
  const seen = new Set<string>();
  const out: Communication[] = [];
  for (const m of messages) {
    if (!m.id) {
      out.push(m);
      continue;
    }
    if (seen.has(m.id)) continue;
    seen.add(m.id);
    out.push(m);
  }
  return out;
}

export async function fetchThreadMessages(
  medplum: MedplumClient,
  threadId: string,
): Promise<Communication[]> {
  const rows = await medplum.searchResources('Communication', {
    'part-of': `Communication/${threadId}`,
    _sort: 'sent',
    _count: '200',
  });
  return dedupeCommunicationsById(rows);
}

/** Fetch replies to a specific message (children via partOf). */
export async function fetchRepliesForMessage(
  medplum: MedplumClient,
  parentMessageId: string,
): Promise<Communication[]> {
  return medplum.searchResources('Communication', {
    'part-of': `Communication/${parentMessageId}`,
    _sort: 'sent',
    _count: '100',
  });
}

/** Batch-count replies for a set of message IDs. */
export async function fetchReplyCountsForMessages(
  medplum: MedplumClient,
  messageIds: string[],
): Promise<Record<string, number>> {
  const counts: Record<string, number> = {};
  await Promise.all(
    messageIds.map(async (id) => {
      try {
        const replies = await medplum.searchResources('Communication', {
          'part-of': `Communication/${id}`,
          _count: '100',
        });
        counts[id] = replies.length;
      } catch {
        counts[id] = 0;
      }
    }),
  );
  return counts;
}

// ── Participant resolution ────────────────────────────────────────────────────

/** Reference strings for Patient or Practitioner (human participants, not Group). */
export function isDirectMessagingParticipantRef(ref: string | undefined): boolean {
  return ref != null && (ref.startsWith('Patient/') || ref.startsWith('Practitioner/'));
}

function isDirectParticipantRef(ref: string | undefined): boolean {
  return isDirectMessagingParticipantRef(ref);
}

/** Collect deduped Patient/Practitioner refs from a thread root (sender, recipient, subject). */
export function collectDirectParticipantRefsFromThread(thread: Communication): Reference[] {
  const seen = new Set<string>();
  const result: Reference[] = [];
  const add = (ref: Reference | undefined): void => {
    const r = ref?.reference;
    if (!r || !isDirectParticipantRef(r) || seen.has(r)) return;
    seen.add(r);
    result.push(ref as Reference);
  };
  add(thread.sender as Reference | undefined);
  for (const recipient of thread.recipient ?? []) {
    add(recipient);
  }
  const subjectRef = thread.subject?.reference;
  if (subjectRef && isDirectParticipantRef(subjectRef)) {
    add(thread.subject as Reference);
  }
  return result;
}

function findCounterpartyParticipant(
  participants: Reference[],
  currentUserReference: string | undefined,
): Reference | undefined {
  if (!currentUserReference) return participants[0];
  const others = participants.filter((p) => p.reference !== currentUserReference);
  if (others.length === 0) return undefined;
  if (currentUserReference.startsWith('Practitioner/')) {
    return others.find((p) => p.reference?.startsWith('Patient/')) ?? others[0];
  }
  if (currentUserReference.startsWith('Patient/')) {
    return others.find((p) => p.reference?.startsWith('Practitioner/')) ?? others[0];
  }
  return others[0];
}

/**
 * Resolve the direct human participants of a thread.
 *
 * - If `threadRoot.recipient[]` contains Patient/Practitioner refs, returns those.
 * - If `threadRoot.subject.reference` is `Group/{id}`, reads the Group and returns
 *   `group.member[].entity` refs for Patient/Practitioner members.
 * - Deduplicates references. Excludes `Group/{id}` refs from the returned list.
 */
export async function resolveThreadParticipants(
  medplum: MedplumClient,
  threadRoot: Communication,
): Promise<Reference[]> {
  const seen = new Set<string>();
  const result: Reference[] = [];

  const add = (ref: Reference): void => {
    const r = ref.reference;
    if (!r || !isDirectParticipantRef(r) || seen.has(r)) return;
    seen.add(r);
    result.push(ref);
  };

  for (const r of threadRoot.recipient ?? []) {
    add(r);
  }

  if (threadRoot.sender) {
    add(threadRoot.sender as Reference);
  }

  const subjectRef = threadRoot.subject?.reference;
  if (subjectRef?.startsWith('Group/') && result.length === 0) {
    const groupId = subjectRef.replace('Group/', '');
    try {
      const group = await medplum.readResource('Group', groupId);
      for (const m of group.member ?? []) {
        if (m.entity) add(m.entity as Reference);
      }
    } catch {
      // Group read failed — fall through with whatever we have
    }
  }

  return result;
}

/**
 * Find the Patient reference among participants for `meta.account` tagging.
 *
 * Medplum's AccessPolicy `compartment: { reference: '%patient' }` causes
 * the server to gate subscription delivery and reads on `meta.account`.
 * Resources created by a Provider won't have the Patient's account unless
 * we set it explicitly. Without this, the Patient's WebSocket subscription
 * silently drops Communications created by other participants.
 */
function findPatientAccountRef(participants: Reference[]): Reference | undefined {
  return participants.find((r) => r.reference?.startsWith('Patient/'));
}

// ── Mutations ────────────────────────────────────────────────────────────────

export interface CreateThreadParams {
  senderRef: Reference;
  recipients: Reference[];
  categoryCode: string;
  categoryDisplay: string;
  topicText?: string;
  subjectRef?: Reference;
  initialMessage: string;
  /** Practice tag code for meta.tag scoping (e.g. 'practice-001'). */
  practiceTagCode?: string;
  /** Space auto-membership rules (spaces only). */
  spaceRules?: Array<{ type: string; value: string }>;
  /** Organization reference for Group.managingEntity (spaces). */
  managingEntity?: Reference;
}

/** Cast a generic Reference to the narrower Communication sender type. Safe when ref is Patient/Practitioner/etc. */
function asSender(ref: Reference): SenderRef {
  return ref as SenderRef;
}
function asRecipients(refs: Reference[]): RecipientRef[] {
  return refs as RecipientRef[];
}

/** Message `recipient` should list other parties; thread roots may duplicate the sender — exclude self. */
function recipientsExcludingSender(sender: Reference, recipients: Reference[]): Reference[] {
  const sr = sender.reference;
  if (!sr || recipients.length === 0) return recipients;
  const filtered = recipients.filter((r) => r.reference && r.reference !== sr);
  return filtered.length > 0 ? filtered : recipients;
}

/** Derive Group type from conversation category. */
function groupTypeForCategory(categoryCode: string): 'person' | 'practitioner' {
  return categoryCode === MSG.CATEGORY.CLIENT ? MSG_GROUP.TYPE_CLIENT : MSG_GROUP.TYPE_PROVIDER;
}

/**
 * Create a new conversation: Group (membership) → Communication header → first message.
 * The Group is the stable identity; the Communication header references it via subject + recipient.
 */
export async function createThread(
  medplum: MedplumClient,
  params: CreateThreadParams,
): Promise<{ thread: Communication; message: Communication; group: FhirGroup }> {
  const now = new Date().toISOString();
  const allParticipants = [params.senderRef, ...params.recipients];

  // 1. Create Group (membership container)
  const groupType = groupTypeForCategory(params.categoryCode);
  const groupName =
    params.topicText ??
    (params.categoryCode === MSG.CATEGORY.CLIENT
      ? params.recipients[0]?.display
      : undefined);

  const group = await createConversationGroup(medplum, {
    type: groupType,
    members: allParticipants,
    name: groupName,
    managingEntity: params.managingEntity,
    practiceTagCode: params.practiceTagCode,
    spaceRules: params.spaceRules,
  });

  const groupRef: Reference = {
    reference: `Group/${group.id}`,
    display: groupName,
  };

  // 2. Create conversation header (thread root)
  const senderRefStr = params.senderRef.reference;
  const readByInitial =
    senderRefStr ? JSON.stringify({ [senderRefStr]: now }) : undefined;

  // Tag with Patient's meta.account so the Patient's compartment-gated
  // subscription receives events even when a Provider creates the resource.
  const patientAccountRef = findPatientAccountRef(allParticipants);
  const metaAccount = patientAccountRef?.reference
    ? { account: { reference: patientAccountRef.reference } }
    : undefined;
  const meta = {
    ...(params.practiceTagCode
      ? { tag: [{ system: MSG_GROUP.PRACTICE_TAG_SYSTEM, code: params.practiceTagCode }] }
      : {}),
    ...metaAccount,
  };
  const hasMeta = Object.keys(meta).length > 0;

  const thread = await medplum.createResource<Communication>({
    resourceType: 'Communication',
    ...(hasMeta ? { meta } : {}),
    identifier: [{ system: MSG.IDENTIFIER_SYSTEM, value: MSG.THREAD_IDENTIFIER_VALUE }],
    status: 'in-progress',
    category: [
      {
        coding: [
          {
            system: MSG.CATEGORY_SYSTEM,
            code: params.categoryCode,
            display: params.categoryDisplay,
          },
        ],
      },
    ],
    ...(params.topicText ? { topic: { text: params.topicText } } : {}),
    subject: groupRef as Communication['subject'],
    sender: asSender(params.senderRef),
    recipient: asRecipients([groupRef, ...allParticipants]),
    sent: now,
    payload: [{ contentString: params.initialMessage }],
    ...(readByInitial
      ? { extension: [{ url: MSG.EXT.READ_BY_USER, valueString: readByInitial }] }
      : {}),
  });

  // 3. Create first message
  const message = await medplum.createResource<Communication>({
    resourceType: 'Communication',
    ...(hasMeta ? { meta } : {}),
    identifier: [{ system: MSG.IDENTIFIER_SYSTEM, value: MSG.MESSAGE_IDENTIFIER_VALUE }],
    partOf: [{ reference: `Communication/${thread.id}` }],
    status: 'completed',
    sender: asSender(params.senderRef),
    recipient: asRecipients(recipientsExcludingSender(params.senderRef, allParticipants)),
    sent: now,
    payload: [{ contentString: params.initialMessage }],
    subject: groupRef as Communication['subject'],
  });

  return { thread, message, group };
}

// ── Quote / content helpers ───────────────────────────────────────────────────

/** Extract the first text content from a message's payload. */
export function getMessageTextContent(message: Communication): string {
  for (const p of message.payload ?? []) {
    if (p.contentString != null && p.contentString.trim() !== '') {
      return p.contentString;
    }
  }
  return '';
}

/** Build a human-readable summary for attachment(s) in a message. */
export function getMessageAttachmentSummary(message: Communication): string {
  const attachments = (message.payload ?? []).filter((p) => p.contentAttachment);
  if (attachments.length === 0) return '';
  if (attachments.length === 1) {
    const a = attachments[0].contentAttachment!;
    const ct = a.contentType ?? '';
    if (ct.startsWith('image/')) return `Image: ${a.title ?? 'image'}`;
    return `Attachment: ${a.title ?? 'file'}`;
  }
  return `${attachments.length} attachments`;
}

/**
 * Build a quoted message snapshot for persisting on a reply.
 * Handles text-only, attachment-only, and mixed messages.
 * Includes image URL when the quoted message has an image attachment.
 */
export function buildQuotedMessageSnapshot(
  message: Communication,
): { id: string; sender: string; content: string; imageUrl?: string } {
  const text = getMessageTextContent(message);
  const attachSummary = getMessageAttachmentSummary(message);
  const imageAttachment = (message.payload ?? []).find(
    (p) => p.contentAttachment?.contentType?.startsWith('image/'),
  )?.contentAttachment;
  return {
    id: message.id ?? '',
    sender: message.sender?.display ?? 'Unknown',
    content: text || attachSummary || '',
    ...(imageAttachment?.url ? { imageUrl: imageAttachment.url } : {}),
  };
}

/**
 * Read quote metadata from a message (new JSON extension or legacy extensions).
 * Returns null if message has no quote.
 */
export function readQuotedMessageSnapshot(
  message: Communication,
): { id: string; sender: string; content: string; imageUrl?: string } | null {
  const extensions = message.extension ?? [];
  const quotedId = extensions.find((e) => e.url === MSG.EXT.QUOTED_MESSAGE_ID)?.valueString;
  if (!quotedId) {
    const inResp = message.inResponseTo?.[0];
    if (inResp?.reference) {
      return {
        id: inResp.reference.replace('Communication/', ''),
        sender: inResp.display ?? 'Unknown',
        content: '',
      };
    }
    return null;
  }
  const imageUrl = extensions.find((e) => e.url === MSG.EXT.QUOTED_IMAGE_URL)?.valueString;
  return {
    id: quotedId,
    sender: extensions.find((e) => e.url === MSG.EXT.QUOTED_SENDER)?.valueString ?? 'Unknown',
    content: extensions.find((e) => e.url === MSG.EXT.QUOTED_CONTENT)?.valueString ?? '',
    ...(imageUrl ? { imageUrl } : {}),
  };
}

// ── Attachments ──────────────────────────────────────────────────────────────

/** Per PRD_MESSAGING_FHIR_MAPPING.md — attachments section. */
/** Keep messaging attachments under Medplum's 1 MiB decoded base64Binary validation cap. */
export const MESSAGING_MAX_ATTACHMENT_BYTES = 700 * 1024;
export const MESSAGING_MAX_ATTACHMENTS_PER_MESSAGE = 10;

const ALLOWED_MESSAGING_ATTACHMENT_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

/** Infer MIME type when the browser leaves `File.type` empty (common on mobile / HEIC rename). */
export function inferMessagingAttachmentContentType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const byExt: Record<string, string> = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  };
  return byExt[ext] ?? 'application/octet-stream';
}

export function validateMessagingAttachmentFile(file: File): void {
  const contentType = inferMessagingAttachmentContentType(file);
  if (!ALLOWED_MESSAGING_ATTACHMENT_TYPES.has(contentType)) {
    throw new Error(
      `"${file.name}" is not an allowed file type. Use PNG, JPEG, GIF, WebP, PDF, or Word documents.`,
    );
  }
  if (file.size > MESSAGING_MAX_ATTACHMENT_BYTES) {
    throw new Error(`"${file.name}" exceeds the 700 KB size limit.`);
  }
}

/** Upload a file and return its Communication payload entry. Uses createAttachment (recommended over deprecated createBinary). */
export async function uploadAttachment(
  medplum: MedplumClient,
  file: File,
  securityContext: Reference,
): Promise<NonNullable<Communication['payload']>[number]> {
  validateMessagingAttachmentFile(file);
  const contentType = inferMessagingAttachmentContentType(file);
  const attachment = await createSecuredAttachment(
    medplum,
    file,
    securityContext,
    file.name,
    contentType,
  );
  return {
    contentAttachment: {
      ...attachment,
      size: file.size,
      title: file.name,
      creation: new Date().toISOString(),
    },
  };
}

/** Send a message into an existing thread (or sub-thread). Updates the thread root preview. */
export async function sendMessageToThread(
  medplum: MedplumClient,
  params: {
    threadId: string;
    senderRef: Reference;
    recipients: Reference[];
    content: string;
    attachmentPayloads?: NonNullable<Communication['payload']>;
    parentMessageId?: string;
    quotedMessage?: { id: string; sender: string; content: string; imageUrl?: string };
    messageCategory?: CodeableConcept[];
    extraExtensions?: NonNullable<Communication['extension']>;
  },
): Promise<Communication> {
  const now = new Date().toISOString();
  const payload: NonNullable<Communication['payload']> = [];
  if (params.content.trim()) {
    payload.push({ contentString: params.content });
  }
  if (params.attachmentPayloads) {
    payload.push(...params.attachmentPayloads);
  }

  const partOfRef = params.parentMessageId
    ? `Communication/${params.parentMessageId}`
    : `Communication/${params.threadId}`;

  const extensions: NonNullable<Communication['extension']> = [...(params.extraExtensions ?? [])];
  if (params.quotedMessage) {
    extensions.push(
      { url: MSG.EXT.QUOTED_MESSAGE_ID, valueString: params.quotedMessage.id },
      { url: MSG.EXT.QUOTED_SENDER, valueString: params.quotedMessage.sender },
      { url: MSG.EXT.QUOTED_CONTENT, valueString: params.quotedMessage.content },
    );
    if (params.quotedMessage.imageUrl) {
      extensions.push({ url: MSG.EXT.QUOTED_IMAGE_URL, valueString: params.quotedMessage.imageUrl });
    }
  }

  let threadRoot: Communication | undefined;
  try {
    threadRoot = await medplum.readResource('Communication', params.threadId);
  } catch {
    // continue; message may still be created without subject (policies may vary)
  }

  // Resolve direct human participants from thread root / Group membership.
  // Never trust caller-provided recipients alone — stale threads may only have Group refs.
  let resolvedRecipients = params.recipients;
  if (threadRoot) {
    try {
      const directParticipants = await resolveThreadParticipants(medplum, threadRoot);
      if (directParticipants.length > 0) {
        resolvedRecipients = directParticipants;
      }
    } catch {
      // fall through to caller-provided recipients
    }
  }

  const messageRecipients = recipientsExcludingSender(params.senderRef, resolvedRecipients);

  // Tag with Patient's meta.account so the Patient's compartment-gated
  // subscription receives the event even when a Provider creates the resource.
  const patientAccountRef = findPatientAccountRef(resolvedRecipients)
    ?? findPatientAccountRef([params.senderRef]);
  const metaAccount = patientAccountRef?.reference
    ? { account: { reference: patientAccountRef.reference } }
    : undefined;

  const message = await medplum.createResource<Communication>({
    resourceType: 'Communication',
    ...(metaAccount ? { meta: metaAccount } : {}),
    identifier: [{ system: MSG.IDENTIFIER_SYSTEM, value: MSG.MESSAGE_IDENTIFIER_VALUE }],
    partOf: [{ reference: partOfRef }],
    status: 'completed',
    sender: asSender(params.senderRef),
    recipient: asRecipients(messageRecipients),
    sent: now,
    payload,
    ...(threadRoot?.subject ? { subject: threadRoot.subject } : {}),
    ...(params.parentMessageId
      ? { inResponseTo: [{ reference: `Communication/${params.parentMessageId}` }] }
      : {}),
    ...(params.messageCategory ? { category: params.messageCategory } : {}),
    ...(extensions.length > 0 ? { extension: extensions } : {}),
  });

  // Update thread root: preview, activity time, and sender read cursor (sender sees own message as read).
  // Sub-thread replies (parentMessageId set) do NOT touch the root — they shouldn't affect the main
  // chat's unread state or sidebar preview.
  if (!params.parentMessageId && threadRoot) {
    try {
      const map = parseReadByUserMap(threadRoot);
      const sr = params.senderRef.reference;
      if (sr) {
        map[sr] = now;
      }
      const ext = [...(threadRoot.extension ?? [])];
      const iBy = ext.findIndex((e) => e.url === MSG.EXT.READ_BY_USER);
      const byUser = { url: MSG.EXT.READ_BY_USER, valueString: JSON.stringify(map) };
      if (iBy >= 0) ext[iBy] = byUser;
      else ext.push(byUser);
      const previewLabel = buildThreadRootPreviewLabel(params.content, params.attachmentPayloads);
      await medplum.updateResource({
        ...threadRoot,
        sent: now,
        payload: [{ contentString: previewLabel || '(attachment)' }],
        extension: ext,
      } as Communication);
    } catch {
      // Non-critical — thread root preview may be stale until next refresh
    }
  }

  return message;
}

/** Parse per-user read timestamps from thread root extension (JSON map of reference → ISO time). */
export function parseReadByUserMap(comm: Communication): Record<string, string> {
  const raw = comm.extension?.find((e) => e.url === MSG.EXT.READ_BY_USER)?.valueString;
  if (!raw) return {};
  try {
    const o = JSON.parse(raw) as Record<string, string>;
    return o && typeof o === 'object' ? o : {};
  } catch {
    return {};
  }
}

/**
 * True if this participant has “seen” the latest thread activity (`thread.sent`), i.e. no unread dot.
 * Senders get their cursor bumped on send; others must open the thread (markThreadRootRead) or send.
 */
export function isParticipantCaughtUpWithThread(
  comm: Communication,
  currentUserReference: string | undefined,
): boolean {
  if (!currentUserReference) return true;

  const hasUnreadExt = comm.extension?.find(e => e.url === MSG.EXT.HAS_UNREAD);
  if (hasUnreadExt?.valueBoolean === true) return false;
  if (hasUnreadExt?.valueBoolean === false) return true;

  const lastActivity = comm.sent;
  if (!lastActivity) return true;

  const readMap = parseReadByUserMap(comm);
  const myRead = readMap[currentUserReference];

  if (!myRead) {
    const hasPerUserReadExtension =
      comm.extension?.some((e) => e.url === MSG.EXT.READ_BY_USER && e.valueString != null && e.valueString !== '') ??
      false;
    if (hasPerUserReadExtension) {
      // Thread uses per-user read map; missing entry means this participant has not read latest activity.
      return false;
    }
    const legacyGlobalRead =
      comm.extension?.some((ext) => ext.url === MSG.EXT.READ_AT && ext.valueDateTime) ?? false;
    return legacyGlobalRead;
  }

  return new Date(myRead).getTime() >= new Date(lastActivity).getTime();
}

/**
 * Text shown in the inbox list for a thread root. Mirrors the latest activity on the thread
 * (`Communication.payload` on the conversation-topic root), scanning all payload parts so we don’t
 * show stale text when [0] isn’t the last snippet (e.g. multi-part payloads).
 */
export function getThreadSidebarPreview(comm: Communication, maxLen = 40): string {
  const parts = comm.payload ?? [];
  let text: string | undefined;
  for (let i = parts.length - 1; i >= 0; i--) {
    const p = parts[i];
    if (p.contentString != null && String(p.contentString).trim() !== '') {
      text = p.contentString;
      break;
    }
  }
  if (text) {
    return text.length > maxLen ? `${text.slice(0, maxLen)}...` : text;
  }
  if (parts.some((p) => p.contentAttachment)) {
    return '(attachment)';
  }
  return 'No message content';
}

/** Label stored on the thread root when a message is sent (text or attachment-only). */
export function buildThreadRootPreviewLabel(
  content: string,
  attachmentPayloads: NonNullable<Communication['payload']> | undefined,
): string {
  const t = content.trim();
  if (t) return t;
  if (attachmentPayloads?.some((p) => p.contentAttachment)) return '(attachment)';
  return '';
}

/**
 * When inbox alignment bumps a stale `thread.sent` to the latest direct message’s `sent`,
 * keep `readByUser` in sync: anyone who was already caught up with the **old** root `sent` must
 * remain caught up with the **new** activity time. Otherwise `markThreadRootRead` (which uses the
 * pre-align root) + refetch looks unread again in the sidebar. Now only bumps the
 * sender via `bumpSingleReaderOnThread` so other participants correctly see unread.
 */
/** Bump only one participant's readByUser entry on a thread root (in-memory). */
function bumpSingleReaderOnThread(
  thread: Communication,
  readerRef: string,
  timestamp: string,
): Communication {
  const map = parseReadByUserMap(thread);
  map[readerRef] = timestamp;
  const ext = [...(thread.extension ?? [])];
  const iBy = ext.findIndex((e) => e.url === MSG.EXT.READ_BY_USER);
  const byUser = { url: MSG.EXT.READ_BY_USER, valueString: JSON.stringify(map) };
  if (iBy >= 0) ext[iBy] = byUser;
  else ext.push(byUser);
  return { ...thread, extension: ext };
}

/**
 * When the thread root wasn’t PATCHed after a send (policy/network), `sent` + preview can lag the
 * latest **direct** child message. Merge from that message so the inbox row matches reality.
 *
 * Only the **sender** of the latest message gets their readByUser bumped (they should not see
 * their own message as unread).  Other participants keep their existing read cursor so they
 * correctly see the new message as unread until they open the conversation.
 */
export async function alignThreadRootsWithLatestDirectMessage(
  medplum: MedplumClient,
  threads: Communication[],
): Promise<Communication[]> {
  const out = await Promise.all(
    threads.map(async (t) => {
      if (!t.id) return t;
      try {
        const direct = await medplum.searchResources('Communication', {
          'part-of': `Communication/${t.id}`,
          identifier: `${MSG.IDENTIFIER_SYSTEM}|${MSG.MESSAGE_IDENTIFIER_VALUE}`,
          _sort: '-sent',
          _count: '1',
        });
        const msg = direct[0];
        if (!msg?.sent) return t;
        const rootMs = t.sent ? new Date(t.sent).getTime() : 0;
        const msgMs = new Date(msg.sent).getTime();
        if (msgMs <= rootMs + 500) return t;
        const withSent: Communication = {
          ...t,
          sent: msg.sent,
          payload: msg.payload ?? t.payload,
        };
        const senderRef = msg.sender?.reference;
        if (senderRef) {
          return bumpSingleReaderOnThread(withSent, senderRef, msg.sent);
        }
        return withSent;
      } catch {
        return t;
      }
    }),
  );
  return out;
}

/** Number of threads the viewer has not caught up with (sidebar unread dot). */
export function countUnreadThreadsForParticipant(
  threads: Communication[],
  currentUserReference: string | undefined,
): number {
  if (!currentUserReference) return 0;
  return threads.reduce(
    (n, t) => n + (isParticipantCaughtUpWithThread(t, currentUserReference) ? 0 : 1),
    0,
  );
}

/**
 * Count total unread messages across all threads for nav badge.
 * For each thread where the user hasn't caught up, counts messages sent after
 * the user's read cursor. Falls back to 1 per unread thread if timestamps aren't available.
 */
export async function countUnreadMessagesForParticipant(
  medplum: MedplumClient,
  threads: Communication[],
  currentUserReference: string | undefined,
): Promise<number> {
  if (!currentUserReference || threads.length === 0) return 0;

  let total = 0;
  const unreadThreads = threads.filter(
    (t) => !isParticipantCaughtUpWithThread(t, currentUserReference),
  );

  await Promise.all(
    unreadThreads.map(async (t) => {
      if (!t.id) {
        total += 1;
        return;
      }
      const readMap = parseReadByUserMap(t);
      const myReadTime = readMap[currentUserReference];

      if (!myReadTime) {
        // Never read — count all messages in thread
        try {
          const msgs = await medplum.searchResources('Communication', {
            'part-of': `Communication/${t.id}`,
            identifier: `${MSG.IDENTIFIER_SYSTEM}|${MSG.MESSAGE_IDENTIFIER_VALUE}`,
            _count: '100',
          });
          // Exclude messages sent by the user themselves
          const fromOthers = msgs.filter((m) => m.sender?.reference !== currentUserReference);
          total += Math.max(fromOthers.length, 1);
        } catch {
          total += 1;
        }
        return;
      }

      // Count messages sent after read cursor by other participants
      try {
        const msgs = await medplum.searchResources('Communication', {
          'part-of': `Communication/${t.id}`,
          identifier: `${MSG.IDENTIFIER_SYSTEM}|${MSG.MESSAGE_IDENTIFIER_VALUE}`,
          _count: '100',
        });
        const unreadMsgs = msgs.filter((m) => {
          if (m.sender?.reference === currentUserReference) return false;
          if (!m.sent) return false;
          return new Date(m.sent).getTime() > new Date(myReadTime).getTime();
        });
        total += Math.max(unreadMsgs.length, 1);
      } catch {
        total += 1;
      }
    }),
  );

  return total;
}

/** @deprecated Use isParticipantCaughtUpWithThread; kept for grep clarity */
export function hasThreadBeenReadByCurrentUser(
  comm: Communication,
  currentUserReference: string | undefined,
): boolean {
  return isParticipantCaughtUpWithThread(comm, currentUserReference);
}

/** Mock/local: merge sender read time onto thread root for sidebar parity. */
export function withBumpedSenderRead(
  thread: Communication,
  senderReference: string | undefined,
  activityTime: string,
  previewPayload?: Communication['payload'],
): Communication {
  if (!senderReference) return thread;
  const map = parseReadByUserMap(thread);
  map[senderReference] = activityTime;
  const ext = [...(thread.extension ?? [])];
  const iBy = ext.findIndex((e) => e.url === MSG.EXT.READ_BY_USER);
  const byUser = { url: MSG.EXT.READ_BY_USER, valueString: JSON.stringify(map) };
  if (iBy >= 0) ext[iBy] = byUser;
  else ext.push(byUser);
  return {
    ...thread,
    sent: activityTime,
    ...(previewPayload ? { payload: previewPayload } : {}),
    extension: ext,
  };
}

/** Local/server: set this participant’s read cursor to at least `thread.sent` (and current time when later). */
export function mergeReaderReadOnThread(
  thread: Communication,
  readerReference: string,
  readAt: string = new Date().toISOString(),
): Communication {
  const map = parseReadByUserMap(thread);
  const lastActivity = thread.sent;
  const t =
    lastActivity && new Date(readAt).getTime() < new Date(lastActivity).getTime()
      ? lastActivity
      : readAt;
  map[readerReference] = t;
  const ext = [...(thread.extension ?? [])];
  const iBy = ext.findIndex((e) => e.url === MSG.EXT.READ_BY_USER);
  const byUser = { url: MSG.EXT.READ_BY_USER, valueString: JSON.stringify(map) };
  if (iBy >= 0) ext[iBy] = byUser;
  else ext.push(byUser);
  return { ...thread, extension: ext };
}

/**
 * Persist “read” for this viewer on the thread Communication (`readByUser` JSON map).
 * Requires write access on Communication (see patient AccessPolicy).
 */
export async function markThreadRootRead(
  medplum: MedplumClient,
  threadId: string,
  readerReference: string,
): Promise<boolean> {
  try {
    const thread = (await medplum.readResource('Communication', threadId)) as Communication;
    const updated = mergeReaderReadOnThread(thread, readerReference);
    await medplum.updateResource(updated);
    return true;
  } catch (e) {
    console.warn('[Messaging] markThreadRootRead failed', e);
    return false;
  }
}

// ── Profile helpers ──────────────────────────────────────────────────────────

export function getProfileReference(
  profile: { resourceType: string; id?: string; name?: unknown },
): Reference {
  const display = profile.resourceType === 'Practitioner'
    ? formatPractitionerPreferredLabel(profile as Practitioner)
    : getProfileDisplayName(profile);
  return {
    reference: `${profile.resourceType}/${profile.id}`,
    ...(display ? { display } : {}),
  };
}

export function getProfileDisplayName(
  profile: { resourceType: string; name?: unknown },
): string {
  const names = (profile as { name?: Array<{ given?: string[]; family?: string; text?: string }> })
    .name;
  if (!names?.length) return 'Unknown';
  const n = names[0];
  if (n.text) return n.text;
  const parts = [...(n.given ?? []), n.family].filter(Boolean);
  return parts.join(' ') || 'Unknown';
}

/**
 * Category + subject for a new thread from compose. Patient→provider must use client-thread
 * with subject = patient (not the provider).
 */
export function getComposeThreadMeta(
  profile: { resourceType: string; id?: string; name?: unknown } | undefined,
  recipient: Reference,
  recipientResourceType: 'Patient' | 'Practitioner' | undefined,
): { categoryCode: string; categoryDisplay: string; subjectRef: Reference | undefined } {
  if (profile?.resourceType === 'Patient' && profile.id) {
    return {
      categoryCode: MSG.CATEGORY.CLIENT,
      categoryDisplay: 'Client Conversation',
      subjectRef: getProfileReference(profile),
    };
  }
  if (profile?.resourceType === 'Practitioner') {
    if (recipientResourceType === 'Patient') {
      return {
        categoryCode: MSG.CATEGORY.CLIENT,
        categoryDisplay: 'Client Conversation',
        subjectRef: recipient,
      };
    }
    if (recipientResourceType === 'Practitioner') {
      return {
        categoryCode: MSG.CATEGORY.PROVIDER,
        categoryDisplay: 'Provider Conversation',
        subjectRef: recipient,
      };
    }
  }
  const ref = recipient.reference ?? '';
  if (ref.startsWith('Group/') || ref.startsWith('CareTeam/')) {
    return {
      categoryCode: MSG.CATEGORY.GROUP,
      categoryDisplay: 'Space',
      subjectRef: recipient,
    };
  }
  return {
    categoryCode: MSG.CATEGORY.PROVIDER,
    categoryDisplay: 'Provider Conversation',
    subjectRef: recipient,
  };
}

export function isThreadRoot(comm: Communication): boolean {
  return (
    comm.identifier?.some(
      (id) =>
        id.system === MSG.IDENTIFIER_SYSTEM &&
        (id.value === MSG.THREAD_IDENTIFIER_VALUE || id.value === MSG.LEGACY_THREAD_IDENTIFIER_VALUE),
    ) ?? false
  );
}

/** True when `display` is a human name, not a bare FHIR reference string. */
export function isUsableParticipantDisplay(
  reference: string | undefined,
  display: string | undefined,
): boolean {
  const d = display?.trim();
  if (!d || !reference) return false;
  if (d === reference) return false;
  if (/^(Patient|Practitioner|Group|CareTeam)\//.test(d)) return false;
  return true;
}

export function participantDisplayLabel(ref: Reference | undefined): string | undefined {
  if (!ref?.reference) return undefined;
  return isUsableParticipantDisplay(ref.reference, ref.display) ? ref.display!.trim() : undefined;
}

export function needsParticipantDisplayResolution(label: string): boolean {
  return label === 'Unknown' || /^(Patient|Practitioner)\//.test(label);
}

export async function fetchParticipantDisplayName(
  medplum: MedplumClient,
  reference: string,
): Promise<string | undefined> {
  const match = /^(Patient|Practitioner)\/(.+)$/.exec(reference);
  if (!match) return undefined;
  const resourceType = match[1] as 'Patient' | 'Practitioner';
  const id = match[2];
  try {
    const resource = await medplum.readResource(resourceType, id);
    const name = getProfileDisplayName(resource);
    return name === 'Unknown' ? undefined : name;
  } catch {
    return undefined;
  }
}

/**
 * Sidebar / header title: show the *other* participant, not `subject` alone.
 * For Group-based threads, `subject` is `Group/{id}` and members come from the Group.
 * For legacy threads, falls back to scanning sender + recipient[].
 * `groupMembers` is optional; when provided, used for Group-based conversations.
 */
export function getConversationCounterpartyDisplay(
  thread: Communication,
  currentUserReference: string | undefined,
  groupMembers?: Reference[],
): string {
  const topic = thread.topic?.text?.trim();
  if (topic) return topic;

  if (!currentUserReference) {
    return participantDisplayLabel(thread.subject as Reference | undefined) ?? 'Unknown';
  }

  const directFromMembers = (groupMembers ?? []).filter((m) =>
    isDirectParticipantRef(m.reference),
  );
  const pool =
    directFromMembers.length > 0
      ? directFromMembers
      : collectDirectParticipantRefsFromThread(thread);

  const other = findCounterpartyParticipant(pool, currentUserReference);
  const preferredLabel = participantDisplayLabel(other);
  if (preferredLabel) return preferredLabel;

  for (const candidate of pool) {
    if (candidate.reference === currentUserReference) continue;
    const label = participantDisplayLabel(candidate);
    if (label) return label;
  }

  const subjectLabel = participantDisplayLabel(thread.subject as Reference | undefined);
  if (subjectLabel) return subjectLabel;

  return 'Unknown';
}

/**
 * Same participant resolution as {@link getConversationCounterpartyDisplay}, but returns
 * the counterparty FHIR reference (e.g. `Patient/abc`) for profile navigation.
 */
export function getConversationCounterpartyReference(
  thread: Communication,
  currentUserReference: string | undefined,
  groupMembers?: Reference[],
): string | undefined {
  if (thread.topic?.text?.trim()) return undefined;

  if (!currentUserReference) {
    const subjectRef = thread.subject?.reference;
    return subjectRef?.startsWith('Patient/') || subjectRef?.startsWith('Practitioner/')
      ? subjectRef
      : undefined;
  }

  const directFromMembers = (groupMembers ?? []).filter((m) =>
    isDirectParticipantRef(m.reference),
  );
  const pool =
    directFromMembers.length > 0
      ? directFromMembers
      : collectDirectParticipantRefsFromThread(thread);

  return findCounterpartyParticipant(pool, currentUserReference)?.reference;
}

export async function resolveConversationCounterpartyDisplay(
  medplum: MedplumClient,
  thread: Communication,
  currentUserReference: string | undefined,
  groupMembers?: Reference[],
): Promise<string> {
  const syncLabel = getConversationCounterpartyDisplay(thread, currentUserReference, groupMembers);
  if (!needsParticipantDisplayResolution(syncLabel)) return syncLabel;

  const counterpartyRef = getConversationCounterpartyReference(
    thread,
    currentUserReference,
    groupMembers,
  );
  if (!counterpartyRef) return syncLabel;

  return (await fetchParticipantDisplayName(medplum, counterpartyRef)) ?? syncLabel;
}

/** Client chart URL for a messaging participant; staff only, Patient refs only. */
export function getClientChartHrefForMessagingParticipant(
  reference: string | undefined,
  isViewerClient: boolean,
): string | undefined {
  if (isViewerClient || !reference?.startsWith('Patient/')) return undefined;
  const id = reference.slice('Patient/'.length).trim();
  return id ? `/dashboard/clients/${id}` : undefined;
}

/** Find an existing client-conversation thread between the user and a patient. */
export async function findClientThreadForPatient(
  medplum: MedplumClient,
  userReference: string,
  patientId: string,
): Promise<Communication | undefined> {
  const patientRef = `Patient/${patientId}`;
  try {
    const groups = await fetchConversationGroupsForUser(medplum, userReference);
    for (const group of groups) {
      if (!group.id) continue;
      const members = group.member ?? [];
      const hasPatient = members.some((m) => m.entity?.reference === patientRef);
      const hasUser = members.some((m) => m.entity?.reference === userReference);
      if (!hasPatient || !hasUser) continue;

      const header = await fetchConversationHeaderForGroup(medplum, group.id);
      if (!header?.id) continue;

      const code = header.category?.[0]?.coding?.[0]?.code;
      if (code === MSG.CATEGORY.CLIENT || code === MSG.LEGACY_CATEGORY.CLIENT) {
        return header;
      }
    }
  } catch {
    return undefined;
  }
  return undefined;
}

/** Find an existing client-conversation thread between a patient and a practitioner. */
export async function findClientThreadForPractitioner(
  medplum: MedplumClient,
  patientReference: string,
  practitionerId: string,
): Promise<Communication | undefined> {
  const practitionerRef = `Practitioner/${practitionerId}`;
  try {
    const groups = await fetchConversationGroupsForUser(medplum, patientReference);
    for (const group of groups) {
      if (!group.id) continue;
      const members = group.member ?? [];
      const hasPatient = members.some((m) => m.entity?.reference === patientReference);
      const hasPractitioner = members.some((m) => m.entity?.reference === practitionerRef);
      if (!hasPatient || !hasPractitioner) continue;

      const header = await fetchConversationHeaderForGroup(medplum, group.id);
      if (!header?.id) continue;

      const code = header.category?.[0]?.coding?.[0]?.code;
      if (code === MSG.CATEGORY.CLIENT || code === MSG.LEGACY_CATEGORY.CLIENT) {
        return header;
      }
    }
  } catch {
    return undefined;
  }
  return undefined;
}

/** Legacy thread roots without a Group subject (pre-migration or provider-initiated). */
async function findLegacyClientThreadBetweenPatientAndPractitioner(
  medplum: MedplumClient,
  patientReference: string,
  practitionerRef: string,
): Promise<Communication | undefined> {
  try {
    const candidates = await medplum.searchResources('Communication', {
      identifier: `${MSG.IDENTIFIER_SYSTEM}|${MSG.THREAD_IDENTIFIER_VALUE},${MSG.IDENTIFIER_SYSTEM}|${MSG.LEGACY_THREAD_IDENTIFIER_VALUE}`,
      recipient: patientReference,
      _count: '100',
    });

    for (const thread of candidates) {
      if (!isThreadRoot(thread)) continue;
      const code = thread.category?.[0]?.coding?.[0]?.code;
      if (code !== MSG.CATEGORY.CLIENT && code !== MSG.LEGACY_CATEGORY.CLIENT) continue;

      const refs = new Set(
        collectDirectParticipantRefsFromThread(thread)
          .map((r) => r.reference)
          .filter(Boolean) as string[],
      );
      if (refs.has(patientReference) && refs.has(practitionerRef)) {
        return thread;
      }
    }
  } catch {
    return undefined;
  }
  return undefined;
}

/**
 * Find an existing client↔provider thread (Group-based or legacy), reusing chat history
 * instead of creating a duplicate thread.
 */
export async function findClientThreadBetweenPatientAndPractitioner(
  medplum: MedplumClient,
  patientReference: string,
  practitionerId: string,
): Promise<Communication | undefined> {
  const fromGroup = await findClientThreadForPractitioner(medplum, patientReference, practitionerId);
  if (fromGroup?.id) return fromGroup;

  const practitionerRef = `Practitioner/${practitionerId}`;
  return findLegacyClientThreadBetweenPatientAndPractitioner(
    medplum,
    patientReference,
    practitionerRef,
  );
}

/** Create a client conversation with Group + thread root only (no first message). */
export async function createEmptyClientThread(
  medplum: MedplumClient,
  params: {
    senderRef: Reference;
    /** Other direct participant (typically Practitioner for client-initiated threads). */
    counterpartyRef?: Reference;
    /** @deprecated Use counterpartyRef — kept for backward compatibility. */
    patientRef?: Reference;
    practiceTagCode?: string;
  },
): Promise<Communication> {
  const counterpartyRef = params.counterpartyRef ?? params.patientRef;
  if (!counterpartyRef) {
    throw new Error('createEmptyClientThread requires counterpartyRef');
  }
  const { senderRef, practiceTagCode } = params;
  const now = new Date().toISOString();
  const allParticipants = [senderRef, counterpartyRef];

  const clientParticipant = allParticipants.find((r) => r.reference?.startsWith('Patient/'));
  const groupDisplay = clientParticipant?.display ?? counterpartyRef.display;

  const group = await createConversationGroup(medplum, {
    type: MSG_GROUP.TYPE_CLIENT,
    members: allParticipants,
    name: groupDisplay,
    practiceTagCode,
  });

  const groupRef: Reference = {
    reference: `Group/${group.id}`,
    display: groupDisplay,
  };

  const senderRefStr = senderRef.reference;
  const readByInitial =
    senderRefStr ? JSON.stringify({ [senderRefStr]: now }) : undefined;

  const patientAccountRef = findPatientAccountRef(allParticipants);
  const metaAccount = patientAccountRef?.reference
    ? { account: { reference: patientAccountRef.reference } }
    : undefined;
  const meta = {
    ...(practiceTagCode
      ? { tag: [{ system: MSG_GROUP.PRACTICE_TAG_SYSTEM, code: practiceTagCode }] }
      : {}),
    ...metaAccount,
  };
  const hasMeta = Object.keys(meta).length > 0;

  return medplum.createResource<Communication>({
    resourceType: 'Communication',
    ...(hasMeta ? { meta } : {}),
    identifier: [{ system: MSG.IDENTIFIER_SYSTEM, value: MSG.THREAD_IDENTIFIER_VALUE }],
    status: 'in-progress',
    category: [
      {
        coding: [
          {
            system: MSG.CATEGORY_SYSTEM,
            code: MSG.CATEGORY.CLIENT,
            display: 'Client Conversation',
          },
        ],
      },
    ],
    subject: groupRef as Communication['subject'],
    sender: asSender(senderRef),
    recipient: asRecipients([groupRef, ...allParticipants]),
    sent: now,
    ...(readByInitial
      ? { extension: [{ url: MSG.EXT.READ_BY_USER, valueString: readByInitial }] }
      : {}),
  });
}

// ── Backfill / repair ────────────────────────────────────────────────────────

/**
 * Idempotent repair: find Group-based thread roots whose `recipient` only contains
 * `Group/{id}` and patch them to `[Group/{id}, ...Group.member.entity]`.
 *
 * Safe to call on every startup or seed — skips threads that already include
 * direct participant refs.
 */
export async function repairGroupOnlyThreadRoots(
  medplum: MedplumClient,
): Promise<{ repaired: number; skipped: number }> {
  let repaired = 0;
  let skipped = 0;

  const threads = await medplum.searchResources('Communication', {
    identifier: `${MSG.IDENTIFIER_SYSTEM}|${MSG.THREAD_IDENTIFIER_VALUE},${MSG.IDENTIFIER_SYSTEM}|${MSG.LEGACY_THREAD_IDENTIFIER_VALUE}`,
    _count: '200',
  });

  for (const thread of threads) {
    const recipients = thread.recipient ?? [];
    const hasDirectParticipant = recipients.some(
      (r) => r.reference && isDirectParticipantRef(r.reference),
    );
    if (hasDirectParticipant) {
      skipped++;
      continue;
    }

    const subjectRef = thread.subject?.reference;
    if (!subjectRef?.startsWith('Group/')) {
      skipped++;
      continue;
    }

    try {
      const participants = await resolveThreadParticipants(medplum, thread);
      if (participants.length === 0) {
        skipped++;
        continue;
      }

      const groupRef = recipients.find((r) => r.reference?.startsWith('Group/'));
      const newRecipients = groupRef
        ? [groupRef, ...participants]
        : [...participants];

      await medplum.updateResource({
        ...thread,
        recipient: asRecipients(newRecipients),
      } as Communication);
      repaired++;
    } catch {
      skipped++;
    }
  }

  return { repaired, skipped };
}
